import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, MAIN_SITE_URL, categories } from '@/data/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const currentLang = SUPPORTED_LANGS.find((l) => l.code === lang) ?? SUPPORTED_LANGS[0];

  const switchLang = (code: string) => {
    const segments = location.pathname.split('/').filter(Boolean);
    const rest = segments.length > 1 ? segments.slice(1).join('/') : '';
    navigate(`/${code}${rest ? `/${rest}` : ''}`);
    setLangOpen(false);
    setMenuOpen(false);
  };

  const navLinks = [
    { label: t('nav.home'), to: `/${lang}` },
    { label: t('nav.shoppingGuide'), to: `/${lang}/buyer-guide` },
    { label: t('nav.articles'), to: `/${lang}/spreadsheet-guide` },
    { label: t('nav.qc'), to: `#qc` },
    { label: t('nav.shipping'), to: `#shipping` },
    { label: t('nav.faq'), to: `#faq` },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
      <nav className="mx-auto max-w-7xl px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        <Link to={`/${lang}`} className="flex items-center gap-2.5 shrink-0">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
            <i className="ri-grid-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg text-foreground-950">{t('brand.name')}</span>
            <span className="text-[11px] text-foreground-500">{t('brand.tagline')}</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-0.5">
          {navLinks.map((link) =>
            link.to.startsWith('#') ? (
              <a
                key={link.label}
                href={link.to}
                className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.to}
                className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Language"
            >
              <i className="ri-global-line text-base w-4 h-4 flex items-center justify-center" />
              <span>{currentLang.short}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-36 rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                {SUPPORTED_LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => switchLang(l.code)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer ${
                      l.code === currentLang.code ? 'text-primary-600 font-semibold' : 'text-foreground-700'
                    }`}
                  >
                    <span>{l.label}</span>
                    {l.code === currentLang.code && <i className="ri-check-line" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('buttons.buyNow')}
            <i className="ri-external-link-line" />
          </a>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            aria-label="Menu"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) =>
              link.to.startsWith('#') ? (
                <a
                  key={link.label}
                  href={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-bold uppercase tracking-wide text-foreground-500">
              {t('nav.browse')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <i className={`${c.icon} text-primary-500 w-4 h-4 flex items-center justify-center shrink-0`} />
                  <span className="truncate">{t(c.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('buttons.buyNow')}
          </a>
        </div>
      )}
    </header>
  );
}